import { z } from 'zod';
// Past workflow runs: the actions agents ran during conversations, pre-call and post-call workflows, and Action
// Studio and standalone workflows. Via lists them from the browser, and reads one on the server, where secret values
// are taken out before it sees anything.
const DEFAULT_LIMIT = 30;
const TOP_NAMES = 10;
const runKindSchema = z.enum(['action', 'pre-call', 'post-call', 'action studio', 'workflow', 'sentinel']);
const runStatusSchema = z.enum(['completed', 'failed', 'running']);
const conversationRefSchema = z.object({ kind: z.enum(['call', 'chat']), conversationId: z.string() });
const runSummarySchema = z.object({
    workflowRunId: z.string(),
    kind: runKindSchema,
    name: z.string(),
    status: runStatusSchema.describe('failed means at least one step failed'),
    startedAt: z.string(),
    agentId: z.string().optional(),
    conversation: conversationRefSchema.optional()
});
export const listWorkflowRunsToolSchema = {
    name: 'list_workflow_runs',
    description: 'List workflow runs, newest first: actions agents ran during conversations, pre-call and post-call workflows, and Action Studio and standalone workflows. Each has its conversation, name, status and start time; failed means a step failed. The counts by status and by name show which actions or workflows fail most. Read one step by step with get_workflow_run.',
    inputSchema: z.object({
        from: z.string().optional().describe('ISO date or date-time; defaults to 7 days ago'),
        to: z.string().optional().describe('ISO date or date-time; defaults to now'),
        kind: runKindSchema.optional(),
        status: runStatusSchema.optional(),
        agentId: z.string().optional().describe('From list_agents'),
        name: z.string().optional().describe('Part of the action or workflow name'),
        conversationId: z.string().optional().describe('Only runs from this call or chat'),
        limit: z.number().int().min(1).max(100).optional().describe(`Defaults to ${DEFAULT_LIMIT}`)
    }),
    outputSchema: z.object({
        runs: z.array(runSummarySchema),
        matched: z.number().describe('How many matched before the limit'),
        counts: z.object({
            byStatus: z.record(z.string(), z.number()),
            byName: z
                .array(z.object({ name: z.string(), total: z.number(), failed: z.number() }))
                .describe(`The ${TOP_NAMES} most frequent names among the matches`)
        })
    })
};
export const runStepSchema = z.object({
    stepId: z.string(),
    name: z.string(),
    kind: z.string().describe('The step type, as app / action'),
    depth: z.number().describe('0 for top-level steps; steps inside a branch or loop sit deeper'),
    status: z.enum(['done', 'failed', 'path taken', 'path not taken', 'not run', 'waiting']),
    ms: z.number().optional().describe('How long it took'),
    output: z.string().optional().describe('What it returned, shortened'),
    error: z
        .object({
        message: z.string(),
        httpStatus: z.number().optional(),
        response: z.string().optional().describe('The body the other service answered with, shortened')
    })
        .optional(),
    conditions: z
        .array(z.object({
        group: z.number(),
        check: z.string().describe('The condition as written'),
        value: z.string().optional().describe('The value it compared, as the run resolved it'),
        met: z.boolean()
    }))
        .optional()
        .describe('Path steps: the path is taken when every condition in one group is met')
});
export const getWorkflowRunToolSchema = {
    name: 'get_workflow_run',
    description: "Read one workflow run step by step: which steps ran, what each returned, which failed and why (with the HTTP status and response body of a failed request), and for branches which path each took, with the values its conditions compared. Secret values show as [secret NAME]: don't guess them. Get run ids from get_conversation (each action's workflowRunId, and the pre-call and post-call runs) or from list_workflow_runs.",
    inputSchema: z.object({ workflowRunId: z.string() }),
    outputSchema: z.object({
        workflowRunId: z.string(),
        kind: runKindSchema,
        name: z.string(),
        status: runStatusSchema,
        conversation: conversationRefSchema.optional(),
        finishedAt: z.string().optional(),
        failedStepId: z.string().optional().describe('The first step that failed'),
        steps: z.array(runStepSchema)
    })
};
export const workflowRunKind = (run) => {
    if (run.preCallWorkflowId || run.preCallWorkflowName)
        return 'pre-call';
    if (run.actionWorkflowId)
        return 'action studio';
    if (run.standaloneWorkflowId)
        return 'workflow';
    if (run.sentinelWorkflowId)
        return 'sentinel';
    if (run.toolCallId || run.toolName)
        return 'action';
    return 'post-call';
};
const RUN_NAMES_BY_KIND = { sentinel: 'Sentinel workflow' };
export const workflowRunName = (run) => run.toolName ??
    run.preCallWorkflowName ??
    run.actionWorkflowName ??
    run.standaloneWorkflowName ??
    RUN_NAMES_BY_KIND[workflowRunKind(run)] ??
    'Post-call workflow';
export const workflowRunConversation = ({ callId, chatId }) => {
    if (callId)
        return { kind: 'call', conversationId: callId };
    if (chatId)
        return { kind: 'chat', conversationId: chatId };
    return undefined;
};
const RUN_STATUSES = {
    completed: 'completed',
    partial: 'failed',
    running: 'running'
};
export const summarizeWorkflowRun = (run) => {
    const conversation = workflowRunConversation(run);
    return {
        workflowRunId: run.workflowRunId,
        kind: workflowRunKind(run),
        name: workflowRunName(run),
        status: RUN_STATUSES[run.status],
        startedAt: new Date(run.startedAt).toISOString(),
        ...(run.assistantId && { agentId: run.assistantId }),
        ...(conversation && { conversation })
    };
};
const DAY_MS = 24 * 60 * 60 * 1000;
/** The date range list_workflow_runs covers, as ISO strings: the input's, or the last 7 days. */
export const workflowRunDateRange = ({ from, to }, now = Date.now()) => ({
    from: new Date(from ? Date.parse(from) : now - 7 * DAY_MS).toISOString(),
    to: new Date(to ? Date.parse(to) : now).toISOString()
});
const countBy = (items, keyOf) => items.reduce((counts, item) => {
    const key = keyOf(item);
    counts[key] = (counts[key] ?? 0) + 1;
    return counts;
}, {});
/** Filters, sorts, counts and trims runs for list_workflow_runs. */
export const findWorkflowRuns = (runs, input, now = Date.now()) => {
    const { from, to } = workflowRunDateRange(input, now);
    const name = input.name?.trim().toLowerCase();
    const matching = runs
        .filter((run) => run.startedAt >= from &&
        run.startedAt <= to &&
        (!input.kind || run.kind === input.kind) &&
        (!input.status || run.status === input.status) &&
        (!input.agentId || run.agentId === input.agentId) &&
        (!name || run.name.toLowerCase().includes(name)) &&
        (!input.conversationId || run.conversation?.conversationId === input.conversationId))
        .sort((a, b) => b.startedAt.localeCompare(a.startedAt));
    const totals = countBy(matching, (run) => run.name);
    const failures = countBy(matching.filter(({ status }) => status === 'failed'), (run) => run.name);
    return {
        runs: matching.slice(0, input.limit ?? DEFAULT_LIMIT),
        matched: matching.length,
        counts: {
            byStatus: countBy(matching, (run) => run.status),
            byName: Object.entries(totals)
                .map(([runName, total]) => ({ name: runName, total, failed: failures[runName] ?? 0 }))
                .sort((a, b) => b.failed - a.failed || b.total - a.total)
                .slice(0, TOP_NAMES)
        }
    };
};
//# sourceMappingURL=workflow-runs.js.map